/**
 * MistakeRetryButton Component
 * Navigates to an evidence-writable Training Session (or Exam Space) — never a thin /quiz deep link.
 */

import { useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import type { Mistake } from '../../lib/api';
import { buildRetentionQuizHref } from '../../features/quiz/retentionQuizLaunch';
import { isQuizRuntimeHref } from '../../features/quiz/quizRuntimeNavigation';
import { useOptionalQuizRuntimeLaunch } from '../../features/quiz/QuizRuntimeLaunchProvider';

interface MistakeRetryButtonProps {
  mistake: Mistake;
  variant?: 'default' | 'compact';
  label?: string;
  questionCount?: number;
  className?: string;
}

export function MistakeRetryButton({
  mistake,
  variant = 'default',
  label,
  questionCount = 1,
  className = '',
}: MistakeRetryButtonProps) {
  const navigate = useNavigate();
  const { launchQuizRuntime } = useOptionalQuizRuntimeLaunch();

  const handleRetry = () => {
    const question = mistake.question;
    const href = buildRetentionQuizHref({
      languageId: question.languageId,
      examType: question.examType,
      skillId: question.section,
      questionCount,
    });
    if (isQuizRuntimeHref(href)) {
      void launchQuizRuntime(href);
      return;
    }
    navigate(href);
  };

  if (variant === 'compact') {
    return (
      <button
        onClick={handleRetry}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-primary-50 text-primary-600 border border-primary-200 hover:bg-primary-100 transition-colors ${className}`}
        title={label ?? '练习本题'}
      >
        <RefreshCw className="h-3 w-3" />
        {label ?? '练习'}
      </button>
    );
  }

  return (
    <button
      onClick={handleRetry}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-[6px] bg-[#e5f4f2] text-[#0f8b8d] text-sm font-semibold border border-[#b9dcd8] hover:bg-[#d0ebE8] transition-colors ${className}`}
    >
      <RefreshCw className="h-4 w-4" />
      {label ?? '练习本题'}
    </button>
  );
}

interface PracticeSimilarButtonProps {
  mistake: Mistake;
  similarCount: number;
  onClick?: () => void;
  className?: string;
}

export function PracticeSimilarButton({
  mistake,
  similarCount,
  onClick,
  className = '',
}: PracticeSimilarButtonProps) {
  const navigate = useNavigate();
  const { launchQuizRuntime } = useOptionalQuizRuntimeLaunch();

  const handlePracticeSimilar = () => {
    const question = mistake.question;
    const countToFetch = Math.min(similarCount, 20);
    const href = buildRetentionQuizHref({
      languageId: question.languageId,
      examType: question.examType,
      skillId: question.section,
      questionCount: countToFetch,
    });
    if (isQuizRuntimeHref(href)) {
      void launchQuizRuntime(href);
    } else {
      navigate(href);
    }

    if (onClick) {
      onClick();
    }
  };

  if (similarCount <= 1) {
    return null;
  }

  return (
    <button
      onClick={handlePracticeSimilar}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-[6px] bg-[#fff2d8] text-[#b86b16] text-sm font-semibold border border-[#f0d293] hover:bg-[#ffe9b8] transition-colors ${className}`}
    >
      <RefreshCw className="h-4 w-4" />
      练习 {similarCount} 道相似题
    </button>
  );
}
